import React, { useState, useEffect } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import signalCampaignService, {
  SignalCampaign,
  LemlistCampaignOption,
} from '../services/signalCampaignService';
import gtmService, { Icp, Persona, Offer } from '../services/gtmService';
import { PageHeader, useConfirm } from '../components/ui';
import { useBerechtigung } from '../berechtigungen/useBerechtigung';
import { RechteHinweis } from '../vertrieb/RechteHinweis';

const STATUS_BADGES: Record<string, { label: string; className: string }> = {
  draft: { label: 'Entwurf', className: 'badge-secondary' },
  collecting: { label: 'Signale erfasst', className: 'badge-info' },
  ready: { label: 'Nachrichten bereit', className: 'badge-info' },
  active: { label: 'Aktiv (versendet)', className: 'badge-success' },
  completed: { label: 'Abgeschlossen', className: 'badge-success' },
};

const SignalCampaignsPage: React.FC = () => {
  const { confirm } = useConfirm();
  // Welle 2: Anlegen und Loeschen verlangen vertrieb.bearbeiten; die
  // Lemlist-Kampagnen (GET /lemlist/campaigns, Versand-Router) nur mit versand.lesen.
  const { darf } = useBerechtigung();
  const darfBearbeiten = darf('vertrieb.bearbeiten');
  const siehtLemlist = darf('versand.lesen');
  const [campaigns, setCampaigns] = useState<SignalCampaign[]>([]);
  const [lemlistCampaigns, setLemlistCampaigns] = useState<LemlistCampaignOption[]>([]);
  const [icps, setIcps] = useState<Icp[]>([]);
  const [personas, setPersonas] = useState<Persona[]>([]);
  const [offers, setOffers] = useState<Offer[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [showCreateForm, setShowCreateForm] = useState(false);
  const navigate = useNavigate();

  const [formData, setFormData] = useState({
    name: '',
    description: '',
    goal: '',
    tone: 'professionell',
    language: 'de',
    variantCount: 2,
    lemlistCampaignId: '',
    icpId: '',
    personaId: '',
    offerId: '',
  });

  useEffect(() => {
    loadCampaigns();
  }, []);

  const loadCampaigns = async () => {
    try {
      const data = await signalCampaignService.getCampaigns();
      setCampaigns(data);
    } catch (err: any) {
      setError(err.response?.data?.error || 'Kampagnen konnten nicht geladen werden');
    } finally {
      setLoading(false);
    }
  };

  const openCreateForm = async () => {
    setShowCreateForm(!showCreateForm);
    if (!showCreateForm && siehtLemlist && lemlistCampaigns.length === 0) {
      try {
        setLemlistCampaigns(await signalCampaignService.getLemlistCampaigns());
      } catch {
        // Lemlist optional at creation time – the field stays free-text-less but usable later.
      }
    }
    if (!showCreateForm && icps.length === 0 && personas.length === 0 && offers.length === 0) {
      try {
        const [i, p, o] = await Promise.all([
          gtmService.getIcps(),
          gtmService.getPersonas(),
          gtmService.getOffers(),
        ]);
        setIcps(i);
        setPersonas(p);
        setOffers(o);
      } catch {
        // GTM grounding is optional.
      }
    }
  };

  const handleCreate = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    try {
      await signalCampaignService.createCampaign({
        name: formData.name,
        description: formData.description || undefined,
        goal: formData.goal || undefined,
        tone: formData.tone || undefined,
        language: formData.language,
        variantCount: formData.variantCount,
        lemlistCampaignId: formData.lemlistCampaignId || undefined,
        icpId: formData.icpId || undefined,
        personaId: formData.personaId || undefined,
        offerId: formData.offerId || undefined,
      });
      setShowCreateForm(false);
      setFormData({
        name: '',
        description: '',
        goal: '',
        tone: 'professionell',
        language: 'de',
        variantCount: 2,
        lemlistCampaignId: '',
        icpId: '',
        personaId: '',
        offerId: '',
      });
      loadCampaigns();
    } catch (err: any) {
      setError(err.response?.data?.error || 'Kampagne konnte nicht erstellt werden');
    }
  };

  const handleDelete = async (campaign: SignalCampaign) => {
    const ok = await confirm({
      title: `Kampagne „${campaign.name}“ löschen?`,
      message: 'Die Kampagne wird mit allen Kontakten, erfassten Signalen und A/B-Varianten dauerhaft gelöscht. Rückgängig nur durch Neuanlage.',
      confirmLabel: 'Löschen',
      tone: 'danger',
    });
    if (!ok) return;
    setError('');
    try {
      await signalCampaignService.deleteCampaign(campaign.id);
      loadCampaigns();
    } catch (err: any) {
      setError(err.response?.data?.error || 'Löschen fehlgeschlagen');
    }
  };

  const getStatusBadge = (status: string) => {
    const info = STATUS_BADGES[status] || { label: status, className: 'badge-secondary' };
    return <span className={`badge ${info.className}`}>{info.label}</span>;
  };

  if (loading) {
    return (
      <div className="loading-container">
        <div className="loading-spinner"></div>
        <div className="loading-text">Lade Kampagnen...</div>
      </div>
    );
  }

  return (
    <div className="page-container">
      <PageHeader
        title="Kaufsignale"
        subtitle="Für Firmen, bei denen gerade etwas passiert: Stellenanzeigen, Förderung, Zertifizierung. Sonst: Zielfirmen recherchieren."
        actions={darfBearbeiten ? (
          <button className="btn btn-success" onClick={openCreateForm}>
            {showCreateForm ? 'Abbrechen' : 'Neue Kampagne'}
          </button>
        ) : undefined}
      />
      <RechteHinweis was="Signal-Kampagnen anlegen und bearbeiten" />

      <p className="text-sm text-muted" style={{ marginBottom: '1.5rem' }}>
        Personalisierte Ansprache auf Basis des letzten LinkedIn-Posts des Prospects,
        mit A/B-Varianten und Erfolgsauswertung über Lemlist.
      </p>

      {error && <div className="alert alert-error">{error}</div>}

      {showCreateForm && darfBearbeiten && (
        <div className="card" style={{ marginBottom: '2rem' }}>
          <form onSubmit={handleCreate}>
            <h3 className="card-title">Neue signalbasierte Kampagne</h3>
            <p className="text-xs text-muted" style={{ marginBottom: '1rem' }}>
              Nachrichten werden mit Claude AI (Opus 4.6) generiert.
            </p>

            <div className="form-group">
              <label>Kampagnenname *</label>
              <input
                type="text"
                className="form-input"
                value={formData.name}
                onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                required
                placeholder="z.B. CTOs SaaS Q3 – Signal-Outreach"
              />
            </div>

            <div className="form-group">
              <label>Beschreibung</label>
              <textarea
                className="form-textarea"
                value={formData.description}
                onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                rows={2}
                placeholder="Kurzbeschreibung der Zielgruppe / Kampagne"
              />
            </div>

            <div className="form-group">
              <label>Ziel / Call-to-Action der Ansprache</label>
              <textarea
                className="form-textarea"
                value={formData.goal}
                onChange={(e) => setFormData({ ...formData, goal: e.target.value })}
                rows={2}
                placeholder="z.B. Ein 15-minütiges Gespräch über Lead-Automatisierung vereinbaren"
              />
            </div>

            <div style={{ display: 'flex', gap: '1rem', flexWrap: 'wrap' }}>
              <div className="form-group" style={{ flex: 1, minWidth: '160px' }}>
                <label>Tonalität</label>
                <input
                  type="text"
                  className="form-input"
                  value={formData.tone}
                  onChange={(e) => setFormData({ ...formData, tone: e.target.value })}
                  placeholder="z.B. professionell, locker, direkt"
                />
              </div>

              <div className="form-group" style={{ flex: 1, minWidth: '120px' }}>
                <label>Sprache</label>
                <select
                  className="form-input"
                  value={formData.language}
                  onChange={(e) => setFormData({ ...formData, language: e.target.value })}
                >
                  <option value="de">Deutsch</option>
                  <option value="en">Englisch</option>
                </select>
              </div>

              <div className="form-group" style={{ flex: 1, minWidth: '140px' }}>
                <label>A/B-Varianten</label>
                <select
                  className="form-input"
                  value={formData.variantCount}
                  onChange={(e) => setFormData({ ...formData, variantCount: Number(e.target.value) })}
                >
                  <option value={2}>2 Varianten (A/B)</option>
                  <option value={3}>3 Varianten (A/B/C)</option>
                  <option value={4}>4 Varianten (A/B/C/D)</option>
                </select>
              </div>
            </div>

            {siehtLemlist && (
              <div className="form-group">
                <label>Lemlist-Kampagne (für Versand)</label>
                <select
                  className="form-input"
                  value={formData.lemlistCampaignId}
                  onChange={(e) => setFormData({ ...formData, lemlistCampaignId: e.target.value })}
                >
                  <option value="">– später zuordnen –</option>
                  {lemlistCampaigns.map((c) => (
                    <option key={c._id} value={c._id}>
                      {c.name}
                    </option>
                  ))}
                </select>
                {lemlistCampaigns.length === 0 && (
                  <p className="text-xs text-muted" style={{ marginTop: '0.25rem' }}>
                    Keine Lemlist-Kampagnen geladen (API-Key prüfen). Zuordnung ist später möglich.
                  </p>
                )}
              </div>
            )}

            <div className="form-group">
              <label>GTM-Grounding (optional)</label>
              <p className="text-xs text-muted" style={{ marginTop: '0.25rem', marginBottom: '0.5rem' }}>
                ICP / Persona / Angebot aus dem GTM-Setup erden die generierten Opener.
              </p>
              <div className="form-row" style={{ display: 'flex', flexWrap: 'wrap', gap: '0.75rem' }}>
                <select
                  className="form-input"
                  style={{ flex: '1 1 160px' }}
                  value={formData.icpId}
                  onChange={(e) => setFormData({ ...formData, icpId: e.target.value })}
                >
                  <option value="">– ICP –</option>
                  {icps.map((i) => (
                    <option key={i.id} value={i.id}>
                      {i.name}
                    </option>
                  ))}
                </select>
                <select
                  className="form-input"
                  style={{ flex: '1 1 160px' }}
                  value={formData.personaId}
                  onChange={(e) => setFormData({ ...formData, personaId: e.target.value })}
                >
                  <option value="">– Persona –</option>
                  {personas.map((p) => (
                    <option key={p.id} value={p.id}>
                      {p.name}
                    </option>
                  ))}
                </select>
                <select
                  className="form-input"
                  style={{ flex: '1 1 160px' }}
                  value={formData.offerId}
                  onChange={(e) => setFormData({ ...formData, offerId: e.target.value })}
                >
                  <option value="">– Angebot –</option>
                  {offers.map((o) => (
                    <option key={o.id} value={o.id}>
                      {o.name}
                    </option>
                  ))}
                </select>
              </div>
            </div>

            <div className="card-footer">
              <button type="submit" className="btn btn-primary">Kampagne erstellen</button>
              <button type="button" className="btn btn-secondary" onClick={() => setShowCreateForm(false)}>
                Abbrechen
              </button>
            </div>
          </form>
        </div>
      )}

      {campaigns.length === 0 ? (
        <div className="empty-state">
          <p className="empty-state-description">
            {darfBearbeiten ? 'Noch keine Kampagnen. Erstellen Sie Ihre erste signalbasierte Kampagne!' : 'Noch keine Kampagnen.'}
          </p>
        </div>
      ) : (
        <div className="grid-cards">
          {campaigns.map((campaign) => (
            <div key={campaign.id} className="card">
              <div className="card-header">
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'start' }}>
                  <h3 className="card-title">{campaign.name}</h3>
                  {getStatusBadge(campaign.status)}
                </div>
                {campaign.description && <p className="card-description">{campaign.description}</p>}
                <div className="text-xs text-muted" style={{ marginTop: '0.5rem' }}>
                  <div>Varianten: {campaign.variantCount} · Kontakte: {campaign._count?.contacts ?? 0}</div>
                  <div>
                    Lemlist:{' '}
                    {campaign.lemlistCampaignId
                      ? <Link to={`/lead-sync?kampagne=${encodeURIComponent(campaign.lemlistCampaignId)}`} title="Leads dieser Lemlist-Kampagne">
                          {lemlistCampaigns.find((c) => c._id === campaign.lemlistCampaignId)?.name ?? 'zugeordnet'} · Leads ansehen
                        </Link>
                      : 'nicht zugeordnet'}
                  </div>
                  <div>Erstellt: {new Date(campaign.createdAt).toLocaleDateString()}</div>
                </div>
              </div>
              <div className="card-footer">
                <button
                  className="btn btn-primary btn-sm"
                  onClick={() => navigate(`/signal-campaigns/${campaign.id}`)}
                >
                  Öffnen
                </button>
                {darfBearbeiten && (
                  <button className="btn btn-danger btn-sm" onClick={() => handleDelete(campaign)}>
                    Löschen
                  </button>
                )}
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export default SignalCampaignsPage;
